import { isAfter } from "date-fns";
import { t } from "ttag";

import { usePatientQuery } from "@/app/hooks";
import { usePortalUserStore } from "@/app/stores";
import {
  EmptyMedicationRequestCard,
  MedicationRequestCard,
} from "@/features/my-meds/components";
import { useCurrentMedicationRequests } from "@/features/my-meds/hooks";
import { Alert, Card, CardSkeleton, PillIcon } from "@/shared/ui";
import { formatBackendDateToVerbose } from "@/shared/utils";

export const CurrentMedicationRequests = () => {
  const noActiveSubscriptions = usePortalUserStore(
    (state) => !state.portalUser?.subscriptions.length,
  );

  const {
    data: patient,
    isLoading: isLoadingPatient,
    isSuccess: isSuccessPatient,
  } = usePatientQuery();

  const hasUpcomingThirdInjection =
    patient?.thirdInjectionDate &&
    isAfter(new Date(patient.thirdInjectionDate), new Date());

  const {
    data: currentRequests,
    isLoading: isLoadingCurrentRequests,
    isSuccess: isSuccessCurrentRequests,
  } = useCurrentMedicationRequests();

  if (noActiveSubscriptions) {
    return null;
  }

  if (hasUpcomingThirdInjection) {
    const thirdInjectionDate = formatBackendDateToVerbose(
      patient.thirdInjectionDate,
    );

    return (
      <Alert>
        {t`You will be able to order medication after your third injection date
  on ${thirdInjectionDate}`}
      </Alert>
    );
  }

  const isLoading = isLoadingPatient || isLoadingCurrentRequests;
  const isSuccess = isSuccessPatient && isSuccessCurrentRequests;

  return (
    <Card.Root>
      <Card.Header className="flex-col">
        <Card.Title icon={PillIcon}>
          {t`Current medication requests`}
        </Card.Title>

        <Card.Description>
          {t`You won't be able to request medication until your current request has been delivered.`}
        </Card.Description>
      </Card.Header>

      <Card.Content>
        {isLoading && <CardSkeleton className="px-2" />}

        {isSuccess && (
          <div className="flex flex-col gap-3">
            {currentRequests.length ? (
              currentRequests.map((request) => (
                <MedicationRequestCard
                  key={request.subscription.id}
                  medicationRequest={request}
                />
              ))
            ) : (
              <EmptyMedicationRequestCard />
            )}
          </div>
        )}
      </Card.Content>
    </Card.Root>
  );
};
